import { supabase } from '../lib/supabase';

export const dashboardCustomizationService = {
  // ============================================================================
  // DASHBOARD WIDGETS
  // ============================================================================

  async getAllWidgets() {
    const { data, error } = await supabase
      ?.from('dashboard_widgets')
      ?.select(`
        *,
        widget_configurations (*)
      `)
      ?.order('position_order', { ascending: true });

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      widgetName: row?.widget_name,
      widgetType: row?.widget_type,
      widgetSize: row?.widget_size,
      positionOrder: row?.position_order,
      isVisible: row?.is_visible,
      isDefault: row?.is_default,
      createdBy: row?.created_by,
      configurations: row?.widget_configurations?.map(config => ({
        id: config?.id,
        configKey: config?.config_key,
        configValue: config?.config_value,
        description: config?.description
      })) || [],
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  async getVisibleWidgets() {
    const { data, error } = await supabase
      ?.from('dashboard_widgets')
      ?.select(`
        *,
        widget_configurations (*)
      `)
      ?.eq('is_visible', true)
      ?.order('position_order', { ascending: true });

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      widgetName: row?.widget_name,
      widgetType: row?.widget_type,
      widgetSize: row?.widget_size,
      positionOrder: row?.position_order,
      configurations: row?.widget_configurations?.reduce((acc, config) => {
        acc[config?.config_key] = config?.config_value;
        return acc;
      }, {}) || {},
      createdAt: row?.created_at
    })) || [];
  },

  async createWidget(widget) {
    const { data, error } = await supabase
      ?.from('dashboard_widgets')
      ?.insert({
        widget_name: widget?.widgetName,
        widget_type: widget?.widgetType,
        widget_size: widget?.widgetSize || 'medium',
        position_order: widget?.positionOrder || 0,
        is_visible: widget?.isVisible ?? true,
        is_default: widget?.isDefault ?? false,
        created_by: widget?.createdBy
      })
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      widgetName: data?.widget_name,
      widgetType: data?.widget_type
    };
  },

  async updateWidget(id, updates) {
    const { data, error } = await supabase
      ?.from('dashboard_widgets')
      ?.update({
        widget_name: updates?.widgetName,
        widget_size: updates?.widgetSize,
        position_order: updates?.positionOrder,
        is_visible: updates?.isVisible,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', id)
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      isVisible: data?.is_visible,
      positionOrder: data?.position_order
    };
  },

  async deleteWidget(id) {
    const { error } = await supabase
      ?.from('dashboard_widgets')
      ?.delete()
      ?.eq('id', id);

    if (error) throw error;

    return { success: true };
  },

  async reorderWidgets(widgetOrders) {
    const updates = widgetOrders?.map(({ id, positionOrder }) => ({
      id,
      position_order: positionOrder,
      updated_at: new Date()?.toISOString()
    }));

    const { error } = await supabase
      ?.from('dashboard_widgets')
      ?.upsert(updates);

    if (error) throw error;

    return { success: true };
  },

  // ============================================================================
  // WIDGET CONFIGURATIONS
  // ============================================================================

  async getWidgetConfigurations(widgetId) {
    const { data, error } = await supabase
      ?.from('widget_configurations')
      ?.select('*')
      ?.eq('widget_id', widgetId);

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      widgetId: row?.widget_id,
      configKey: row?.config_key,
      configValue: row?.config_value,
      description: row?.description,
      createdAt: row?.created_at
    })) || [];
  },

  async updateWidgetConfiguration(widgetId, configKey, configValue) {
    const { data, error } = await supabase
      ?.from('widget_configurations')
      ?.upsert({
        widget_id: widgetId,
        config_key: configKey,
        config_value: configValue,
        updated_at: new Date()?.toISOString()
      }, {
        onConflict: 'widget_id,config_key'
      })
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      configKey: data?.config_key,
      configValue: data?.config_value
    };
  },

  // ============================================================================
  // USER WIDGET PREFERENCES
  // ============================================================================

  async getUserWidgetPreferences(userEmail) {
    const { data, error } = await supabase
      ?.from('user_widget_preferences')
      ?.select(`
        *,
        dashboard_widgets (*)
      `)
      ?.eq('user_email', userEmail)
      ?.order('position_order', { ascending: true });

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      widgetId: row?.widget_id,
      isVisible: row?.is_visible,
      positionOrder: row?.position_order,
      customConfig: row?.custom_config,
      widget: row?.dashboard_widgets ? {
        widgetName: row?.dashboard_widgets?.widget_name,
        widgetType: row?.dashboard_widgets?.widget_type,
        widgetSize: row?.dashboard_widgets?.widget_size
      } : null
    })) || [];
  },

  async saveUserWidgetPreference(userEmail, widgetId, preferences) {
    const { data, error } = await supabase
      ?.from('user_widget_preferences')
      ?.upsert({
        user_email: userEmail,
        widget_id: widgetId,
        is_visible: preferences?.isVisible,
        position_order: preferences?.positionOrder,
        custom_config: preferences?.customConfig,
        updated_at: new Date()?.toISOString()
      }, {
        onConflict: 'user_email,widget_id'
      })
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      isVisible: data?.is_visible
    };
  },

  // ============================================================================
  // FILTERED VIEWS
  // ============================================================================

  async getAllFilteredViews(userEmail) {
    const { data, error } = await supabase
      ?.from('filtered_views')
      ?.select('*')
      ?.or(`created_by.eq.${userEmail},is_shared.eq.true`)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      viewName: row?.view_name,
      viewDescription: row?.view_description,
      targetEntity: row?.target_entity,
      filterCriteria: row?.filter_criteria,
      isDefault: row?.is_default,
      isShared: row?.is_shared,
      createdBy: row?.created_by,
      createdAt: row?.created_at
    })) || [];
  },

  async getFilteredViewsByEntity(targetEntity, userEmail) {
    const { data, error } = await supabase
      ?.from('filtered_views')
      ?.select('*')
      ?.eq('target_entity', targetEntity)
      ?.or(`created_by.eq.${userEmail},is_shared.eq.true`)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      viewName: row?.view_name,
      viewDescription: row?.view_description,
      filterCriteria: row?.filter_criteria,
      isDefault: row?.is_default,
      isShared: row?.is_shared,
      createdBy: row?.created_by
    })) || [];
  },

  async createFilteredView(view) {
    const { data, error } = await supabase
      ?.from('filtered_views')
      ?.insert({
        view_name: view?.viewName,
        view_description: view?.viewDescription,
        target_entity: view?.targetEntity,
        filter_criteria: view?.filterCriteria,
        is_default: view?.isDefault ?? false,
        is_shared: view?.isShared ?? false,
        created_by: view?.createdBy
      })
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      viewName: data?.view_name,
      targetEntity: data?.target_entity
    };
  },

  async updateFilteredView(id, updates) {
    const { data, error } = await supabase
      ?.from('filtered_views')
      ?.update({
        view_name: updates?.viewName,
        view_description: updates?.viewDescription,
        filter_criteria: updates?.filterCriteria,
        is_default: updates?.isDefault,
        is_shared: updates?.isShared,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', id)
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      viewName: data?.view_name
    };
  },

  async deleteFilteredView(id) {
    const { error } = await supabase
      ?.from('filtered_views')
      ?.delete()
      ?.eq('id', id);

    if (error) throw error;

    return { success: true };
  },

  // ============================================================================
  // PNG KINA CURRENCY FORMATTING
  // ============================================================================

  formatPngKina(amount, options = {}) {
    const {
      showSymbol = true,
      decimals = 2,
      useCommas = true
    } = options;

    if (amount === null || amount === undefined) return 'K0.00';

    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    
    if (isNaN(numAmount)) return 'K0.00';

    const formatted = numAmount?.toFixed(decimals);
    const parts = formatted?.split('.');
    
    if (useCommas) {
      parts[0] = parts?.[0]?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    const result = parts?.join('.');
    
    return showSymbol ? `K${result}` : result;
  },

  parsePngKina(formattedAmount) {
    if (!formattedAmount) return 0;
    
    const cleaned = formattedAmount
      ?.toString()
      ?.replace(/K/g, '')
      ?.replace(/,/g, '')
      ?.trim();
    
    const parsed = parseFloat(cleaned);
    
    return isNaN(parsed) ? 0 : parsed;
  },

  // ============================================================================
  // REPORT GENERATION WITH PNG FORMAT
  // ============================================================================

  async generateReportWithPngFormat(reportConfig) {
    // This would integrate with a PNG image generation service
    // For now, return configuration for frontend to handle
    return {
      format: 'png',
      config: reportConfig,
      currencyFormat: 'PGK',
      timestamp: new Date()?.toISOString()
    };
  }
};

export default dashboardCustomizationService;